import React, { useState } from 'react';
import {
  Drawer,
  Box,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Typography,
  Collapse,
  IconButton,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import {
  Dashboard,
  People,
  LocalPharmacy,
  Inventory,
  Category,
  Business,
  Assessment,
  ExpandLess,
  ExpandMore,
  ChevronLeft,
  ChevronRight,
  Description,
  ShoppingCart,
  Medication,
  AccountCircle,
  Science,
  Campaign,
  Insights,
} from '@mui/icons-material';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../contexts/AuthContext';

const NAV_BG = '#0D1B35';
const NAV_BORDER = 'rgba(255,255,255,0.07)';
const ICON_ACTIVE = '#FFFFFF';
const ICON_INACTIVE = 'rgba(255,255,255,0.48)';
const TEXT_ACTIVE = '#FFFFFF';
const TEXT_INACTIVE = 'rgba(255,255,255,0.60)';
const ACTIVE_BG = 'rgba(255,255,255,0.13)';
const HOVER_BG = 'rgba(255,255,255,0.06)';
const ACTIVE_HOVER_BG = 'rgba(255,255,255,0.18)';

interface SidebarProps {
  role: 'admin' | 'doctor' | 'consultant';
  mobileOpen: boolean;
  onMobileClose: () => void;
  collapsed: boolean;
  onToggleCollapse: () => void;
  drawerWidth: number;
}

interface MenuItem {
  id: string;
  labelKey: string;
  icon: React.ReactNode;
  path?: string;
  children?: MenuItem[];
  badge?: string;
  comingSoon?: boolean;
}

const adminMenuItems: MenuItem[] = [
  { id: 'dashboard', labelKey: 'sidebar.dashboard', icon: <Dashboard />, path: '/admin/dashboard' },
  { id: 'customer-ops', labelKey: 'sidebar.customerOps', icon: <Insights />, path: '/admin/customer-ops' },
  {
    id: 'users',
    labelKey: 'sidebar.users',
    icon: <People />,
    children: [
      { id: 'all-users', labelKey: 'sidebar.allUsers', icon: <People />, path: '/admin/users' },
      { id: 'create-user', labelKey: 'sidebar.createUser', icon: <People />, path: '/admin/users/create' },
    ],
  },
  {
    id: 'pharmacies',
    labelKey: 'sidebar.pharmacies',
    icon: <LocalPharmacy />,
    path: '/admin/pharmacy-chains',
  },
  {
    id: 'catalog',
    labelKey: 'sidebar.medicationCatalog',
    icon: <Inventory />,
    children: [
      { id: 'products', labelKey: 'sidebar.products', icon: <Inventory />, path: '/admin/catalog' },
      { id: 'pharmaceutical-forms', labelKey: 'sidebar.pharmaceuticalForms', icon: <Medication />, path: '/admin/pharmaceutical-forms' },
      { id: 'dosages', labelKey: 'sidebar.dosages', icon: <Science />, path: '/admin/dosages' },
    ],
  },
  { id: 'specialties', labelKey: 'sidebar.specialties', icon: <Category />, path: '/admin/specialties' },
  { id: 'insurance', labelKey: 'sidebar.insuranceCompanies', icon: <Business />, path: '/admin/insurance-companies' },
  { id: 'pharmaceutical-labs', labelKey: 'sidebar.pharmaceuticalLabs', icon: <Science />, path: '/admin/pharmaceutical-labs' },
  { id: 'promotions', labelKey: 'sidebar.promotions', icon: <Campaign />, path: '/admin/promotions' },
  { id: 'reports', labelKey: 'sidebar.reports', icon: <Assessment />, path: '/admin/reports' },
];

export const Sidebar: React.FC<SidebarProps> = ({
  role,
  mobileOpen,
  onMobileClose,
  collapsed,
  onToggleCollapse,
  drawerWidth,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const navigate = useNavigate();
  const location = useLocation();
  const [expandedItems, setExpandedItems] = useState<string[]>([]);
  const { user } = useAuth();
  const { t } = useTranslation();

  const getMenuItems = (): MenuItem[] => {
    switch (role) {
      case 'admin':
        return adminMenuItems;
      case 'doctor':
        return [
          { id: 'dashboard', labelKey: 'sidebar.dashboard', icon: <Dashboard />, path: '/doctor/dashboard' },
          ...(user?.canPrescribe
            ? [
                {
                  id: 'orders',
                  labelKey: 'sidebar.prescriptions',
                  icon: <ShoppingCart />,
                  children: [
                    { id: 'all-orders', labelKey: 'sidebar.allPrescriptions', icon: <ShoppingCart />, path: '/doctor/orders' },
                    { id: 'create-order', labelKey: 'sidebar.createPrescription', icon: <ShoppingCart />, path: '/doctor/orders/new' },
                    { id: 'templates', labelKey: 'sidebar.templates', icon: <Description />, path: '/doctor/templates' },
                  ],
                },
              ]
            : []),
          ...(user?.canViewPatients
            ? [{ id: 'patients', labelKey: 'sidebar.patients', icon: <People />, path: '/doctor/patients' }]
            : []),
          { id: 'medications', labelKey: 'sidebar.medicationsLookup', icon: <Medication />, path: '/doctor/medications' },
          { id: 'profile', labelKey: 'sidebar.profile', icon: <AccountCircle />, path: '/doctor/profile' },
        ];
      case 'consultant':
        return [
          { id: 'dashboard', labelKey: 'sidebar.dashboard', icon: <Dashboard />, path: '/consultant/dashboard' },
          { id: 'medications', labelKey: 'sidebar.medicationsLookup', icon: <Medication />, path: '/consultant/medications' },
        ];
      default:
        return [];
    }
  };

  const menuItems = getMenuItems();

  const handleItemClick = (item: MenuItem) => {
    if (item.comingSoon) return;
    if (item.children) {
      setExpandedItems((prev) =>
        prev.includes(item.id) ? prev.filter((id) => id !== item.id) : [...prev, item.id]
      );
    } else if (item.path) {
      navigate(item.path);
      if (isMobile) onMobileClose();
    }
  };

  const isActive = (item: MenuItem): boolean => {
    if (item.path) return location.pathname === item.path;
    if (item.children) return item.children.some((child) => child.path === location.pathname);
    return false;
  };

  const renderMenuItem = (item: MenuItem, level: number = 0) => {
    const active = isActive(item);
    const expanded = expandedItems.includes(item.id);
    const hasChildren = item.children && item.children.length > 0;

    return (
      <React.Fragment key={item.id}>
        <ListItem disablePadding sx={{ display: 'block' }}>
          <ListItemButton
            onClick={() => handleItemClick(item)}
            disabled={item.comingSoon}
            sx={{
              minHeight: 50,
              justifyContent: collapsed ? 'center' : 'initial',
              px: collapsed ? 1 : 2,
              pl: !collapsed && level > 0 ? 3.5 : undefined,
              mx: 1,
              borderRadius: '10px',
              bgcolor: active ? ACTIVE_BG : 'transparent',
              transition: 'background-color 120ms ease, transform 80ms ease-out',
              '&:active': { transform: 'scale(0.97)' },
              '&:hover': {
                bgcolor: active ? ACTIVE_HOVER_BG : HOVER_BG,
              },
              '&.Mui-disabled': { opacity: 0.35 },
            }}
          >
            <ListItemIcon
              sx={{
                minWidth: 0,
                mr: collapsed ? 0 : 2.5,
                justifyContent: 'center',
                color: active ? ICON_ACTIVE : ICON_INACTIVE,
                '& .MuiSvgIcon-root': { fontSize: collapsed ? 24 : 20 },
              }}
            >
              {item.icon}
            </ListItemIcon>
            {!collapsed && (
              <>
                <ListItemText
                  primary={t(item.labelKey)}
                  primaryTypographyProps={{
                    fontSize: 13.5,
                    fontWeight: active ? 600 : 400,
                    color: active ? TEXT_ACTIVE : TEXT_INACTIVE,
                    letterSpacing: '0.1px',
                  }}
                />
                {item.comingSoon && (
                  <Typography
                    variant="caption"
                    sx={{
                      bgcolor: 'rgba(255,167,38,0.18)',
                      color: '#FFB951',
                      px: 1,
                      py: 0.25,
                      borderRadius: 1,
                      fontSize: 9,
                      fontWeight: 700,
                      letterSpacing: '0.5px',
                      textTransform: 'uppercase',
                    }}
                  >
                    Soon
                  </Typography>
                )}
                {hasChildren && (
                  expanded
                    ? <ExpandLess sx={{ fontSize: 16, color: ICON_INACTIVE }} />
                    : <ExpandMore sx={{ fontSize: 16, color: ICON_INACTIVE }} />
                )}
              </>
            )}
          </ListItemButton>
        </ListItem>

        {hasChildren && !collapsed && (
          <Collapse in={expanded} timeout="auto" unmountOnExit>
            <List component="div" disablePadding>
              {item.children!.map((child) => renderMenuItem(child, level + 1))}
            </List>
          </Collapse>
        )}
      </React.Fragment>
    );
  };

  const drawerContent = (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', bgcolor: NAV_BG }}>
      {/* Logo / Brand */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: 72,
          px: 1,
          borderBottom: `1px solid ${NAV_BORDER}`,
          position: 'relative',
        }}
      >
        {collapsed ? (
          <img
            src="/logo-icon.svg"
            alt="Lysi"
            style={{ width: 32, height: 32, objectFit: 'contain', filter: 'brightness(0) invert(1)', opacity: 0.9 }}
          />
        ) : (
          <img
            src="/logo-full.svg"
            alt="Lysi-Web"
            style={{ height: 42, width: 'auto', objectFit: 'contain', filter: 'brightness(0) invert(1)', opacity: 0.9 }}
          />
        )}

        {!isMobile && !collapsed && (
          <IconButton
            onClick={onToggleCollapse}
            size="small"
            sx={{ position: 'absolute', right: 8, color: ICON_INACTIVE, '&:hover': { bgcolor: HOVER_BG, color: '#fff' } }}
          >
            <ChevronLeft fontSize="small" />
          </IconButton>
        )}
      </Box>

      {/* Expand button when collapsed */}
      {!isMobile && collapsed && (
        <Box sx={{ px: 1, pt: 1.5 }}>
          <IconButton
            onClick={onToggleCollapse}
            size="small"
            sx={{
              width: '100%',
              color: ICON_INACTIVE,
              borderRadius: '10px',
              '&:hover': { bgcolor: HOVER_BG, color: '#fff' },
            }}
          >
            <ChevronRight fontSize="small" />
          </IconButton>
        </Box>
      )}

      {/* Menu Items */}
      <Box sx={{ flexGrow: 1, overflowY: 'auto', overflowX: 'hidden', py: 1.5 }}>
        <List disablePadding>{menuItems.map((item) => renderMenuItem(item))}</List>
      </Box>

      {/* Footer */}
      {!collapsed && (
        <Box sx={{ px: 2, py: 1.5, borderTop: `1px solid ${NAV_BORDER}` }}>
          <Typography sx={{ fontSize: 10, color: 'rgba(255,255,255,0.22)', letterSpacing: '0.5px', textAlign: 'center' }}>
            v1.0.0 © 2025 LYSI-WEB
          </Typography>
        </Box>
      )}
    </Box>
  );

  return (
    <Box
      component="nav"
      sx={{ width: { md: drawerWidth }, flexShrink: { md: 0 } }}
      aria-label="navigation"
    >
      {/* Mobile drawer */}
      <Drawer
        variant="temporary"
        open={mobileOpen}
        onClose={onMobileClose}
        ModalProps={{ keepMounted: true }}
        sx={{
          display: { xs: 'block', md: 'none' },
          '& .MuiDrawer-paper': {
            boxSizing: 'border-box',
            width: 260,
            bgcolor: NAV_BG,
            borderRight: `1px solid ${NAV_BORDER}`,
          },
        }}
      >
        {drawerContent}
      </Drawer>

      {/* Desktop drawer */}
      <Drawer
        variant="permanent"
        sx={{
          display: { xs: 'none', md: 'block' },
          '& .MuiDrawer-paper': {
            boxSizing: 'border-box',
            width: drawerWidth,
            bgcolor: NAV_BG,
            borderRight: `1px solid ${NAV_BORDER}`,
            top: 0,
            height: '100%',
            transition: theme.transitions.create('width', {
              easing: theme.transitions.easing.sharp,
              duration: theme.transitions.duration.enteringScreen,
            }),
          },
        }}
        open
      >
        {drawerContent}
      </Drawer>
    </Box>
  );
};
